import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DemoModalService } from '../../core/demo-modal.service';

// Vídeo embebido en la sección "Conoce PackCert" (sobre el sistema PackCert).
const SECTION_VIDEO_IDS: Record<string, string> = {
  es: 'bFxsarm3oAg',
  en: 'H66h5kxQUH8',
};

// Vídeo corporativo que se abre en modal ("Ver vídeo corporativo").
const CORP_VIDEO_IDS: Record<string, string> = {
  es: 'LoUpVt88LEc',
  en: 'ZtNp-5S7048',
};

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnDestroy {
  // Logos de clientes para la banda animada (se renderizan dos veces para el loop).
  // Los logos anchos/planos (sm) se muestran algo más pequeños para igualar el
  // peso visual del resto.
  private readonly SMALL_LOGOS = new Set(['apeam', 'asovav', 'biogold', 'urudor', 'usda', 'visualnacert']);
  readonly clientLogos = [
    'apeam', 'asovav', 'avifruit', 'biogold', 'bollo', 'citricom', 'citrogold',
    'clemengold', 'cvvp', 'domaines', 'fruitalyst', 'grapa', 'orc', 'sando',
    'sunworld', 'topfruit', 'urudor', 'usda', 'varietyinnovation', 'visualnacert',
  ].map((n) => ({ src: `assets/img/clientes/${n}.webp`, sm: this.SMALL_LOGOS.has(n) }));

  // Vídeo de la sección "Conoce PackCert" (embebido en el marco)
  playing = false;
  videoUrl: SafeResourceUrl | null = null;

  // Vídeo corporativo (modal)
  corpOpen = false;
  corpUrl: SafeResourceUrl | null = null;

  @ViewChild('videoFrame') private videoFrame?: ElementRef<HTMLElement>;
  private readonly langSub: Subscription;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly translate: TranslateService,
    private readonly demoModal: DemoModalService,
  ) {
    // Al cambiar de idioma con un vídeo en marcha, se recarga con el del nuevo idioma.
    this.langSub = this.translate.onLangChange.subscribe(() => {
      if (this.playing) {
        this.videoUrl = this.buildUrl(SECTION_VIDEO_IDS);
      }
      if (this.corpOpen) {
        this.corpUrl = this.buildUrl(CORP_VIDEO_IDS);
      }
    });
  }

  /** Reproduce el vídeo de la sección "Conoce PackCert" dentro de su propio marco. */
  playVideo(): void {
    if (this.playing) {
      return;
    }
    this.videoUrl = this.buildUrl(SECTION_VIDEO_IDS);
    this.playing = true;
  }

  /** Abre el vídeo corporativo en un modal (según el idioma activo). */
  openCorporateVideo(): void {
    this.corpUrl = this.buildUrl(CORP_VIDEO_IDS);
    this.corpOpen = true;
    document.body.classList.add('modal-active');
  }

  closeCorporateVideo(e?: MouseEvent): void {
    // Si el clic viene de dentro del modal (p. ej. el iframe), no cerrar.
    if (e && e.target !== e.currentTarget) {
      return;
    }
    this.corpOpen = false;
    this.corpUrl = null;
    document.body.classList.remove('modal-active');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.corpOpen) {
      this.closeCorporateVideo();
    }
  }

  /** Botón "Solicitar una demo": abre el modal de solicitud. */
  openDemo(): void {
    this.demoModal.open();
  }

  private buildUrl(ids: Record<string, string>): SafeResourceUrl {
    const id = ids[this.translate.currentLang] ?? ids['es'];
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=1`,
    );
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    document.body.classList.remove('modal-active');
  }
}
